import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";
import { WagmiProvider } from 'wagmi';
import { QueryClientProvider } from '@tanstack/react-query';
import { wagmiAdapter, queryClient } from '@/config/reownConfig';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';

import LandingPage from "@/pages/LandingPage";
import BookingPage from "@/pages/BookingPage";
import PublicMeetingRoomBookingPage from "@/pages/PublicMeetingRoomBookingPage";
import ListHotelPage from "@/pages/ListHotelPage";
import DashboardPage from "@/pages/DashboardPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import GuestPortalPage from "@/pages/GuestPortalPage";
import GuestLoginPage from "@/pages/GuestLoginPage";
import BountyBoardPage from "@/pages/BountyBoardPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import BlockchainDashboardPage from "@/pages/BlockchainDashboardPage";
import FeatureManagementPage from "@/pages/FeatureManagementPage";
import PolicyManagementPage from "@/pages/PolicyManagementPage";
import PlatformDashboardPage from "@/pages/platform-admin/PlatformDashboardPage";
import HotelEnrollmentPage from "@/pages/platform-admin/HotelEnrollmentPage";
import HermesChatPage from "@/pages/platform-admin/HermesChatPage";
import ApprovalDashboardPage from "@/pages/ApprovalDashboardPage";
import ApprovalHistoryPage from "@/pages/ApprovalHistoryPage";
import CriticStatsPage from "@/pages/CriticStatsPage";
import ArgusAttestationQueuePage from "@/pages/ArgusAttestationQueuePage";
import AdminEscalationsPage from "@/pages/AdminEscalationsPage";
import ArgusOnboardingPage from "@/pages/ArgusOnboardingPage";
import BrowserUseConsolePage from "@/pages/BrowserUseConsolePage";
import ComplianceDryRunPage from "@/pages/ComplianceDryRunPage";
import VerifyHotelPage from "@/pages/VerifyHotelPage";
import BioLandingPage from "@/pages/BioLandingPage";
import ConfirmationsPage from "@/pages/ConfirmationsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import MarketplacePage from "@/pages/MarketplacePage";
import CheckInPage from "@/pages/CheckInPage";
import WalkInPage from "@/pages/WalkInPage";
import StaffInboxPage from "@/pages/StaffInboxPage";
import HousekeepingDashboardPage from "@/pages/HousekeepingDashboardPage";
import MaintenanceDashboardPage from "@/pages/MaintenanceDashboardPage";
import ProtocolDashboardPage from "@/pages/ProtocolDashboardPage";
import HotelRegistrationPage from "@/pages/HotelRegistrationPage";
import MarinaEscrowPage from "@/pages/MarinaEscrowPage";
import TreasuryDashboardPage from "@/pages/TreasuryDashboardPage";
import StakingDashboardPage from "@/pages/StakingDashboardPage";
import WhistleblowerPage from "@/pages/WhistleblowerPage";
import HalvingTrackerPage from "@/pages/HalvingTrackerPage";
import CommandCenterPage from "@/pages/CommandCenterPage";
import LinenDashboardPage from "@/pages/LinenDashboardPage";
import AccountAbstractionPage from "@/pages/AccountAbstractionPage";
import DocumentsPage from "@/pages/DocumentsPage";
import GovernanceDashboardPage from "@/pages/GovernanceDashboardPage";
import ProtocolWebsitePage from "@/pages/ProtocolWebsitePage";
import MetaverseViewerPage from "@/pages/MetaverseViewerPage";
import DigitalTwinGeneratorPage from "@/pages/DigitalTwinGeneratorPage";
import MetaverseWorldMapPage from "@/pages/MetaverseWorldMapPage";
import CreatorMarketplacePage from "@/pages/CreatorMarketplacePage";
import FaucetPage from "@/pages/FaucetPage";
import DeploymentDashboardPage from "@/pages/DeploymentDashboardPage";
import WatcherDashboardPage from "@/pages/WatcherDashboardPage";
import MetaverseEconomyPage from "@/pages/MetaverseEconomyPage";
import GovernanceParamsPage from "@/pages/GovernanceParamsPage";
import MemoryDashboardPage from "@/pages/MemoryDashboardPage";
import VoiceMarketplacePage from "@/pages/VoiceMarketplacePage";
import VendorDashboardPage from "@/pages/VendorDashboardPage";
import VendorRevenuePage from "@/pages/VendorRevenuePage";
import GuestProductPage from "@/pages/GuestProductPage";
import VendorShowroomPage from "@/pages/VendorShowroomPage";
import VendorAnalyticsPage from "@/pages/VendorAnalyticsPage";
import GuestEarnPage from "@/pages/GuestEarnPage";
import GetbitsyAdminPage from "@/pages/GetbitsyAdminPage";
import WhyGetbitsyPage from "@/pages/WhyGetbitsyPage";
import MarinaDashboardPreview from "@/pages/MarinaDashboardPreview";
import DeskOverviewPage from "@/pages/DeskOverviewPage";
import RestockReconciliationPage from "@/pages/RestockReconciliationPage";
import ArgusDashboardPage from "@/pages/ArgusDashboardPage";
import StaffDashboardPage from "@/pages/StaffDashboardPage";
import DigitalTwinDashboard from "@/pages/DigitalTwinDashboard";
import GuestFridgePage from "@/pages/GuestFridgePage";
import BryteDashboardPage from "@/pages/BryteDashboardPage";
import GuestTrayPage from "@/pages/GuestTrayPage";
import StaffTrayPage from "@/pages/StaffTrayPage";
import TrayQRAdminPage from "@/pages/TrayQRAdminPage";
import TrayDownloadPage from "@/pages/TrayDownloadPage";
import FrontDeskCheckInPage from "@/pages/FrontDeskCheckInPage";
import AccountRecoveryPage from "@/pages/AccountRecoveryPage";
import TrustedGuestProfilePage from "@/pages/TrustedGuestProfilePage";
import VerifyBadgePage from "@/pages/VerifyBadgePage";
import VenuePartnerSignupPage from "@/pages/VenuePartnerSignupPage";
import DocsPage from "@/pages/DocsPage";
import AgentsIndexPage from "@/pages/AgentsIndexPage";
import AgentDetailPage from "@/pages/AgentDetailPage";
import CompareIndexPage from "@/pages/CompareIndexPage";
import CompareDetailPage from "@/pages/CompareDetailPage";
import RegisterVendorPage from "@/pages/RegisterVendorPage";
import VenuesIndexPage from "@/pages/VenuesIndexPage";
import VenuesCityPage from "@/pages/VenuesCityPage";
import { AuthProvider } from "@/lib/AuthContext";

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <LanguageProvider>
        <CartProvider>
          <WagmiProvider config={wagmiAdapter.wagmiConfig}>
            <QueryClientProvider client={queryClient}>
              <Toaster position="top-right" richColors />
              <BrowserRouter>
              <AuthProvider>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/book/:hotelId" element={<BookingPage />} />
                <Route path="/meeting-rooms/:hotelId" element={<PublicMeetingRoomBookingPage />} />
                <Route path="/list-hotel" element={<ListHotelPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/guest-portal" element={<GuestPortalPage />} />
                <Route path="/guest-login" element={<GuestLoginPage />} />
                <Route path="/bounty-board" element={<BountyBoardPage />} />
                <Route path="/admin" element={<GetbitsyAdminPage />} />
                <Route path="/admin/legacy" element={<AdminDashboardPage />} />
                <Route path="/admin/blockchain" element={<BlockchainDashboardPage />} />
                <Route path="/admin/blockchain/features/:hotelId" element={<FeatureManagementPage />} />
                <Route path="/admin/blockchain/policies/:hotelId" element={<PolicyManagementPage />} />
                <Route path="/admin/blockchain/approvals/:hotelId" element={<ApprovalDashboardPage />} />
                <Route path="/admin/blockchain/approvals/:hotelId/history" element={<ApprovalHistoryPage />} />
                <Route path="/admin/critic-stats" element={<CriticStatsPage />} />
                <Route path="/admin/argus-attestations" element={<ArgusAttestationQueuePage />} />
                <Route path="/admin/escalations" element={<AdminEscalationsPage />} />
                <Route path="/admin/argus-onboarding" element={<ArgusOnboardingPage />} />
                <Route path="/admin/browser-use" element={<BrowserUseConsolePage />} />
                <Route path="/admin/compliance-demo" element={<ComplianceDryRunPage />} />
                <Route path="/verify/hotel/:hotelId" element={<VerifyHotelPage />} />
                <Route path="/bio" element={<BioLandingPage />} />
                <Route path="/genesis-108" element={<BioLandingPage />} />
                <Route path="/genesis" element={<BioLandingPage />} />
                
                {/* Platform Admin Routes */}
                <Route path="/platform-admin" element={<PlatformDashboardPage />} />
                <Route path="/platform-admin/enroll" element={<HotelEnrollmentPage />} />
                <Route path="/platform-admin/hermes" element={<HermesChatPage />} />
                
                <Route path="/hotel/:hotelId/confirmations" element={<ConfirmationsPage />} />
                <Route path="/hotel/:hotelId/analytics" element={<AnalyticsPage />} />
                <Route path="/marketplace" element={<MarketplacePage />} />
                <Route path="/checkin" element={<CheckInPage />} />
                <Route path="/walk-in" element={<WalkInPage />} />
                <Route path="/staff-inbox" element={<StaffInboxPage />} />
                <Route path="/housekeeping" element={<HousekeepingDashboardPage />} />
                <Route path="/maintenance" element={<MaintenanceDashboardPage />} />
                <Route path="/protocol-dashboard" element={<ProtocolDashboardPage />} />
                <Route path="/register-hotel" element={<HotelRegistrationPage />} />
                <Route path="/marina-escrow" element={<MarinaEscrowPage />} />
                <Route path="/treasury" element={<TreasuryDashboardPage />} />
                <Route path="/staking" element={<StakingDashboardPage />} />
                <Route path="/justice" element={<WhistleblowerPage />} />
                <Route path="/halving" element={<HalvingTrackerPage />} />
                <Route path="/command-center" element={<CommandCenterPage />} />
                <Route path="/linen" element={<LinenDashboardPage />} />
                <Route path="/account-abstraction" element={<AccountAbstractionPage />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/governance" element={<GovernanceDashboardPage />} />
                <Route path="/protocol" element={<ProtocolWebsitePage />} />
                <Route path="/metaverse" element={<MetaverseWorldMapPage />} />
                <Route path="/metaverse/:hotelId" element={<MetaverseViewerPage />} />
                <Route path="/metaverse/:hotelId/generate" element={<DigitalTwinGeneratorPage />} />
                <Route path="/creator-marketplace" element={<CreatorMarketplacePage />} />
                <Route path="/faucet" element={<FaucetPage />} />
                <Route path="/admin/deployments" element={<DeploymentDashboardPage />} />
                <Route path="/admin/watcher" element={<WatcherDashboardPage />} />
                <Route path="/metaverse/economy" element={<MetaverseEconomyPage />} />
                <Route path="/governance/parameters" element={<GovernanceParamsPage />} />
                <Route path="/memory" element={<MemoryDashboardPage />} />
                <Route path="/voice-marketplace" element={<VoiceMarketplacePage />} />
                <Route path="/vendors" element={<VendorDashboardPage />} />
                <Route path="/vendors/revenue" element={<VendorRevenuePage />} />
                <Route path="/shop/:token" element={<GuestProductPage />} />
                <Route path="/vendors/:vendorId/showroom" element={<VendorShowroomPage />} />
                <Route path="/vendors/:vendorId/analytics" element={<VendorAnalyticsPage />} />
                <Route path="/earn" element={<GuestEarnPage />} />
                <Route path="/why-getbitsy" element={<WhyGetbitsyPage />} />
                <Route path="/marina" element={<MarinaDashboardPreview />} />
                <Route path="/desk/overview" element={<DeskOverviewPage />} />
                <Route path="/marina/restock" element={<RestockReconciliationPage />} />
                <Route path="/argus" element={<ArgusDashboardPage />} />
                <Route path="/staff" element={<StaffDashboardPage />} />
                <Route path="/twin" element={<DigitalTwinDashboard />} />
                <Route path="/fridge/:hotelId/:roomNumber" element={<GuestFridgePage />} />
                <Route path="/bryte/:hotelId" element={<BryteDashboardPage />} />
                <Route path="/bryte" element={<BryteDashboardPage />} />
                <Route path="/t/:trayId" element={<GuestTrayPage />} />
                <Route path="/s/:trayId" element={<StaffTrayPage />} />
                <Route path="/admin/tray-qr-codes" element={<TrayQRAdminPage />} />
                <Route path="/tray-download" element={<TrayDownloadPage />} />
                <Route path="/front-desk/checkin/:bookingId" element={<FrontDeskCheckInPage />} />
                <Route path="/recover" element={<AccountRecoveryPage />} />
                <Route path="/trusted/:handle" element={<TrustedGuestProfilePage />} />
                <Route path="/verify-badge" element={<VerifyBadgePage />} />
                <Route path="/verify-badge/venue" element={<VenuePartnerSignupPage />} />
                <Route path="/docs" element={<DocsPage />} />
                <Route path="/agents" element={<AgentsIndexPage />} />
                <Route path="/agents/:slug" element={<AgentDetailPage />} />
                <Route path="/compare" element={<CompareIndexPage />} />
                <Route path="/compare/:competitor" element={<CompareDetailPage />} />
                <Route path="/register-vendor" element={<RegisterVendorPage />} />
                <Route path="/venues" element={<VenuesIndexPage />} />
                <Route path="/venues/:citySlug" element={<VenuesCityPage />} />
              </Routes>
              </AuthProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </WagmiProvider>
        </CartProvider>
      </LanguageProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
